import { copyToClipboard } from '@choco-admin/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useNotification } from '../contexts/NotificationContext';

const COPIED_FEEDBACK_MS = 2000;

/**
 * Copies a value and flips `justCopied` for a moment so the control can show
 * a checkmark. When the browser blocks the clipboard, the value goes into the
 * error notification so it can still be copied by hand.
 *
 * @param name How the value is named to a person, e.g. "login email".
 */
export const useCopyToClipboard = (name: string) => {
  const { addNotification } = useNotification();
  const [justCopied, setJustCopied] = useState(false);
  const resetTimeout = useRef<number>();

  useEffect(
    () => () => {
      window.clearTimeout(resetTimeout.current);
    },
    []
  );

  const copy = useCallback(
    async (value?: string | null) => {
      if (!value) return;

      if (await copyToClipboard(value)) {
        window.clearTimeout(resetTimeout.current);
        setJustCopied(true);
        resetTimeout.current = window.setTimeout(
          () => setJustCopied(false),
          COPIED_FEEDBACK_MS
        );
        return;
      }

      addNotification({
        title: 'Could not copy',
        message: `Select the ${name} and copy it by hand: ${value}`,
        type: 'error',
      });
    },
    [addNotification, name]
  );

  return { copy, justCopied };
};
